import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useSearchParams, useParams, useNavigate } from 'react-router-dom';
import { ShieldAlert, Bug } from 'lucide-react';
import toast from '../design-system/toast';
import { Finding, FindingListResponse, BatchAssessment } from '../types/finding';
import { FixResponse } from '../types/fix';
import { FromApplicationContext } from '../types/application';
import { useAuthenticatedApi } from '../hooks/useAuthenticatedApi';
import { useFindingStatusUpdate } from '../hooks/useFindingStatusUpdate';
import { useOrganization } from '../contexts/OrganizationContext';
import { apiEndpoints } from '../utils/api';
import FindingsTable from '../components/findings/FindingsTable';
import FindingStatusReasonModal from '../components/findings/FindingStatusReasonModal';
import AssessmentProgressBanner from '../components/findings/AssessmentProgressBanner';
import { compareBySeverity } from '../components/findings/severityDisplay';
import ScanFilter from '../components/findings/ScanFilter';
import { useScanBatches } from '../hooks/useScanBatches';
import { Spinner } from '../design-system';

type ChipKey = 'actionable' | 'open' | 'needs_manual_review' | 'all';

const CHIPS: { key: ChipKey; label: string; match: (f: Finding) => boolean }[] = [
  { key: 'actionable', label: 'Actionable', match: (f) => f.status === 'open' || f.status === 'needs_manual_review' },
  { key: 'open', label: 'Open', match: (f) => f.status === 'open' },
  { key: 'needs_manual_review', label: 'Needs Review', match: (f) => f.status === 'needs_manual_review' },
  { key: 'all', label: 'All', match: () => true },
];

const FindingsListPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { applicationId: routeApplicationId } = useParams<{ applicationId: string }>();
  const navigate = useNavigate();
  const [findings, setFindings] = useState<Finding[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeAssessments, setActiveAssessments] = useState<BatchAssessment[]>([]);
  const [busyFindingId, setBusyFindingId] = useState<string | null>(null);
  const assessmentPollRef = useRef<NodeJS.Timeout | null>(null);
  const initialLoadDone = useRef(false);
  const api = useAuthenticatedApi();
  const { canWrite } = useOrganization();

  // Merge the updated finding in place so it moves between chips without a refetch.
  const handleStatusUpdated = useCallback((updated: Finding) => {
    setFindings((previous) => previous.map((f) => (f.id === updated.id ? { ...f, ...updated } : f)));
  }, []);
  const { updateStatus, updatingFindingId, reasonPrompt } = useFindingStatusUpdate(handleStatusUpdated);

  // Application comes from the route; severity/scan-type/scan-batch remain URL-driven (deep links).
  const applicationIdFilter = routeApplicationId || searchParams.get('application_id');
  const severityFilter = searchParams.get('severity');
  const scanTypeFilter = searchParams.get('scan_type');
  // Repeatable param: the scan filter can select several scans at once, and the API
  // takes the same shape (?scan_batch_id=a&scan_batch_id=b). Memoised so the fetch
  // effect doesn't see a new array on every render.
  const scanBatchIdFilter = useMemo(() => searchParams.getAll('scan_batch_id'), [searchParams]);
  const branchFilter = searchParams.get('branch') || '';
  const activeChip = (searchParams.get('status') as ChipKey) || 'actionable';

  const { batches } = useScanBatches(applicationIdFilter || undefined);

  const fetchActiveAssessments = useCallback(async () => {
    try {
      const response = await api.get<BatchAssessment[]>(apiEndpoints.findings.activeAssessments());
      setActiveAssessments(response);
      return response;
    } catch (error) {
      console.error('Error fetching active assessments:', error);
      return [];
    }
  }, [api]);

  const fetchFindings = useCallback(async (showLoader = true) => {
    if (showLoader) setIsLoading(true);
    try {
      const params = new URLSearchParams();
      if (applicationIdFilter) params.append('application_id', applicationIdFilter);
      if (severityFilter) params.append('severity', severityFilter);
      if (scanTypeFilter) params.append('scan_type', scanTypeFilter);
      scanBatchIdFilter.forEach((id) => params.append('scan_batch_id', id));
      if (branchFilter) params.append('branch', branchFilter);
      // Fetch all statuses; the status chips filter client-side (and compute counts).
      params.append('limit', '500');

      const response = await api.get<FindingListResponse>(`${apiEndpoints.findings.list()}?${params.toString()}`);
      setFindings(response.findings);
    } catch (error) {
      console.error('Error fetching findings:', error);
      toast.error('Failed to load findings');
    } finally {
      if (showLoader) setIsLoading(false);
    }
  }, [api, applicationIdFilter, severityFilter, scanTypeFilter, scanBatchIdFilter, branchFilter]);

  // Only the first load takes the page over with a spinner; changing a filter refetches
  // in place so the header, chips and filter keep their position under the cursor.
  useEffect(() => {
    if (!initialLoadDone.current) {
      initialLoadDone.current = true;
      fetchFindings(true);
    } else {
      fetchFindings(false);
    }
    fetchActiveAssessments();
  }, [fetchFindings, fetchActiveAssessments]);

  // Poll assessment status while assessments are active; refresh findings on completion.
  useEffect(() => {
    const hasActive = activeAssessments.some((a) => a.status === 'queued' || a.status === 'running');
    if (hasActive) {
      assessmentPollRef.current = setInterval(async () => {
        const assessments = await fetchActiveAssessments();
        const stillActive = assessments.some((a) => a.status === 'queued' || a.status === 'running');
        if (!stillActive) fetchFindings(false);
      }, 3000);
    }
    return () => {
      if (assessmentPollRef.current) {
        clearInterval(assessmentPollRef.current);
        assessmentPollRef.current = null;
      }
    };
  }, [activeAssessments, fetchActiveAssessments, fetchFindings]);

  const selectChip = (key: ChipKey) => {
    const newParams = new URLSearchParams(searchParams);
    if (key === 'actionable') {
      newParams.delete('status'); // actionable is the default
    } else {
      newParams.set('status', key);
    }
    setSearchParams(newParams);
  };

  const setScanBatches = (scanBatchIds: string[]) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('scan_batch_id'); // no scans selected is the default
    scanBatchIds.forEach((id) => newParams.append('scan_batch_id', id));
    setSearchParams(newParams);
  };

  // Row actions — mirror the finding detail page so both entry points behave the same.
  const handleProposeFix = async (finding: Finding) => {
    setBusyFindingId(finding.id);
    try {
      // Needs-review findings are auto-opened before fix creation, matching the detail page.
      if (finding.status === 'needs_manual_review') {
        await api.patch<Finding>(apiEndpoints.findings.updateStatus(finding.id), { status: 'open' });
      }
      const fix = await api.post<FixResponse>(apiEndpoints.findings.autofix(finding.id), {});
      toast.success('Fix created! Redirecting...');
      navigate(`/repositories/${routeApplicationId}/fixes/${fix.id}`);
    } catch (error: any) {
      console.error('Error creating autofix:', error);
      toast.error(error?.message || 'Failed to create fix');
    } finally {
      setBusyFindingId(null);
    }
  };

  const handleViewFix = (finding: Finding) => {
    if (!finding.fix_summary) return;
    navigate(`/repositories/${routeApplicationId}/fixes/${finding.fix_summary.fix_id}`);
  };

  const fromApplication = useMemo<FromApplicationContext>(
    () => ({ appId: routeApplicationId || '', appName: '' }),
    [routeApplicationId],
  );

  const emptyState =
    findings.length > 0
      ? { title: 'No findings match this filter', hint: 'Try a different status filter above.' }
      : scanBatchIdFilter.length > 0
      ? {
          title: scanBatchIdFilter.length === 1 ? 'No findings from this scan' : 'No findings from these scans',
          hint: 'Pick another scan, or clear the scan filter to see every finding.',
        }
      : { title: 'No findings yet', hint: 'Run a scan to surface real vulnerabilities here.' };

  const activeChipDef = CHIPS.find((c) => c.key === activeChip) ?? CHIPS[0];
  const displayedFindings = useMemo(
    () => [...findings.filter(activeChipDef.match)].sort(compareBySeverity),
    [findings, activeChipDef],
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Spinner size={32} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-ui-background-primary">
      <div className="mx-auto w-full max-w-screen-2xl px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* Header */}
        <div className="flex items-center gap-3 mb-5">
          <div className="w-9 h-9 rounded-[10px] bg-ui-background-secondary border border-ui-border-primary flex items-center justify-center flex-shrink-0">
            <Bug className="w-[18px] h-[18px] text-brand-primary" />
          </div>
          <h1 className="text-2xl font-bold tracking-tight text-ui-text-primary">Security Findings</h1>
        </div>

        {/* Status chips + scan filter */}
        <div className="flex items-center justify-between gap-3 flex-wrap mb-5">
          <div className="flex items-center gap-2 flex-wrap">
            {CHIPS.map((chip) => {
              const count = findings.filter(chip.match).length;
              const active = chip.key === activeChip;
              return (
                <button
                  key={chip.key}
                  onClick={() => selectChip(chip.key)}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    active
                      ? 'bg-brand-primary text-black'
                      : 'bg-ui-background-tertiary text-ui-text-secondary hover:text-ui-text-primary'
                  }`}
                >
                  {chip.label}
                  <span className={active ? 'text-black/60' : 'text-ui-text-tertiary'}>{count}</span>
                </button>
              );
            })}
          </div>
          {(batches.length > 0 || scanBatchIdFilter.length > 0) && (
            <ScanFilter batches={batches} value={scanBatchIdFilter} onChange={setScanBatches} />
          )}
        </div>

        {/* Active assessment banners (transient) */}
        {activeAssessments
          .filter((a) => a.status === 'queued' || a.status === 'running')
          .map((assessment) => (
            <div key={assessment.id} className="mb-4">
              <AssessmentProgressBanner assessment={assessment} onComplete={() => fetchFindings(false)} />
            </div>
          ))}

        {/* Findings table */}
        {displayedFindings.length > 0 ? (
          <FindingsTable
            findings={displayedFindings}
            fromApplication={fromApplication}
            canWrite={canWrite}
            busyFindingId={busyFindingId ?? updatingFindingId}
            onProposeFix={handleProposeFix}
            onViewFix={handleViewFix}
            onUpdateStatus={updateStatus}
          />
        ) : (
          <div className="text-center py-16 border border-dashed border-ui-border-primary rounded-2xl">
            <ShieldAlert className="w-9 h-9 text-ui-text-tertiary mx-auto mb-3" />
            <div className="text-[15px] font-semibold text-ui-text-secondary">{emptyState.title}</div>
            <p className="text-sm text-ui-text-tertiary mt-1.5">{emptyState.hint}</p>
          </div>
        )}
      </div>

      {/* Mounted at the page root: the row menu is its own stacking context. */}
      <FindingStatusReasonModal {...reasonPrompt} />
    </div>
  );
};

export default FindingsListPage;
